import { useState, type ReactNode } from "react";
import { ProductType } from "@KassaroAB/shared/types/enums/ProductType";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  type ChangeRequestOperation,
  type ChangeRequestView,
  ChangeRequestOperationType,
  ChangeRequestStatus,
} from "@KassaroAB/shared/types/changeRequest";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import {
  formatChangeRequestValue,
  getOperationBeforeRecord,
  getOperationChanges,
  getOperationCreateRecord,
  getOperationPreviewName,
  getDomainLabelKey,
  getOperationTypeLabelKey,
  getRecordFieldValue,
  groupOperationsByDomain,
  LAYOUT_ITEM_FIELDS,
  humanizeFieldKey,
  summarizeChanges,
} from "../utils/changeRequestFormatters";
import { formatChangeRequestApproveError } from "../utils/formatChangeRequestApproveError";
import { LayoutItemsPreview } from "./LayoutItemsPreview";
import {
  getChangeRequestOperationKind,
  isChangeRequestApprovable,
} from "@KassaroAB/shared/types/changeRequestDomains";

const Wrapper = styled.div`
  max-width: min(100%, 640px);
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
  background-color: ${colors.backgroundDark};
  overflow: hidden;
`;

const HeaderRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
  padding: ${spacing.x2}px ${spacing.x3}px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  color: ${colors.text};
  &:hover {
    background-color: ${colors.background};
  }
`;

const Label = styled.span`
  font-size: ${fontSizes.regular}px;
  font-weight: 600;
  color: ${colors.text};
  flex-shrink: 0;
`;

const StatusPill = styled.span<{ $status: ChangeRequestStatus }>`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 999px;
  flex-shrink: 0;
  background-color: ${({ $status }) => {
    if ($status === ChangeRequestStatus.APPROVED) return "rgba(52,199,89,0.18)";
    if ($status === ChangeRequestStatus.DISCARDED)
      return "rgba(142,142,147,0.18)";
    if ($status === ChangeRequestStatus.PARTIALLY_APPLIED)
      return "rgba(255,159,10,0.18)";
    return "rgba(10,132,255,0.14)";
  }};
  color: ${({ $status }) => {
    if ($status === ChangeRequestStatus.APPROVED) return "#1a8a3a";
    if ($status === ChangeRequestStatus.DISCARDED) return colors.textMuted;
    if ($status === ChangeRequestStatus.PARTIALLY_APPLIED) return "#a05a00";
    return "#0a6fcf";
  }};
`;

const Spacer = styled.span`
  flex: 1;
`;

const Chevron = styled.span`
  flex-shrink: 0;
  display: flex;
  color: ${colors.textMuted};
`;

const PreviewList = styled.div`
  padding: 0 ${spacing.x3}px ${spacing.x2}px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PreviewLine = styled.div<{ $kind: "create" | "update" | "delete" }>`
  font-size: ${fontSizes.small}px;
  color: ${colors.text};
  display: flex;
  align-items: baseline;
  gap: ${spacing.x1}px;
`;

const PreviewBadge = styled.span<{ $kind: "create" | "update" | "delete" }>`
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
  width: 14px;
  color: ${({ $kind }) => {
    if ($kind === "create") return "#1a8a3a";
    if ($kind === "delete") return colors.danger;
    return "#0a6fcf";
  }};
`;

const PreviewName = styled.span`
  color: ${colors.text};
`;

const PreviewMeta = styled.span`
  color: ${colors.textMuted};
  font-size: ${fontSizes.small}px;
`;

const Details = styled.div`
  border-top: 1px solid ${colors.border};
  padding: ${spacing.x3}px;
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
`;

const Card = styled.div`
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.small}px;
  padding: ${spacing.x1}px ${spacing.x2}px;
  background-color: ${colors.background};
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
`;

const InlineFields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.x05}px ${spacing.x3}px;
  font-size: ${fontSizes.small}px;
`;

const InlineField = styled.div`
  display: flex;
  gap: ${spacing.x1}px;
  min-width: 0;
`;

const ItemsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
  font-size: ${fontSizes.small}px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${fontSizes.small}px;
  & th {
    text-align: left;
    font-weight: 600;
    color: ${colors.textMuted};
    padding: ${spacing.x05}px ${spacing.x1}px;
    border-bottom: 1px solid ${colors.border};
  }
  & td {
    padding: ${spacing.x05}px ${spacing.x1}px;
    border-bottom: 1px solid ${colors.border};
    color: ${colors.text};
  }
  & tr:last-child td {
    border-bottom: none;
  }
`;

const ShowDetailsButton = styled.button`
  font-size: ${fontSizes.small}px;
  padding: ${spacing.x1}px ${spacing.x2}px;
  border-radius: ${borderRadius.small}px;
  border: 1px solid ${colors.border};
  background-color: ${colors.background};
  color: ${colors.text};
  cursor: pointer;
`;

const ActionsArea = styled.div`
  padding: ${spacing.x1}px ${spacing.x3}px ${spacing.x2}px;
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
`;

const CardTitle = styled.div`
  font-size: ${fontSizes.small}px;
  font-weight: 600;
  color: ${colors.text};
`;

const FieldLabel = styled.div`
  color: ${colors.textMuted};
  padding-top: 1px;
`;

const FieldValue = styled.div`
  color: ${colors.text};
  word-break: break-word;
`;

const DiffTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
`;

const DiffRow = styled.div`
  display: grid;
  grid-template-columns: minmax(100px, 130px) 1fr 16px 1fr;
  gap: ${spacing.x1}px ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
  align-items: start;
`;

const DiffHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(100px, 130px) 1fr 16px 1fr;
  gap: ${spacing.x1}px ${spacing.x2}px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${colors.textMuted};
`;

const BeforeValue = styled.div`
  color: ${colors.textMuted};
  text-decoration: line-through;
  word-break: break-word;
`;

const AfterValue = styled.div`
  color: ${colors.text};
  font-weight: 600;
  word-break: break-word;
`;

const Arrow = styled.div`
  color: ${colors.textMuted};
  text-align: center;
  padding-top: 1px;
`;

const ApproveRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x2}px;
`;

const ApproveButton = styled.button`
  font-size: ${fontSizes.small}px;
  font-weight: 600;
  padding: ${spacing.x1}px ${spacing.x2}px;
  border-radius: ${borderRadius.small}px;
  border: none;
  cursor: pointer;
  background-color: ${colors.primary};
  color: ${colors.primaryContrastText};
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PREVIEW_LIMIT_PER_DOMAIN = 5;

const isProductCreate = (operation: ChangeRequestOperation) =>
  operation.type === ChangeRequestOperationType.ProductCreate;

const DomainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  & + & {
    margin-top: ${spacing.x1}px;
  }
`;

const DomainHeading = styled.div`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${colors.textMuted};
`;

const AppliedMark = styled.span`
  color: #1a8a3a;
  font-size: ${fontSizes.small}px;
`;

const Hint = styled.div`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const ApproveError = styled.div`
  font-size: ${fontSizes.small}px;
  color: ${colors.danger};
`;

function FieldValueContent({
  changeRequestView,
  fieldKey,
  value,
}: {
  changeRequestView: ChangeRequestView;
  fieldKey: string;
  value: unknown;
}) {
  const formatValue = useFormatValue(changeRequestView);
  if (LAYOUT_ITEM_FIELDS.has(fieldKey) && Array.isArray(value)) {
    return (
      <LayoutItemsPreview
        fieldKey={fieldKey}
        items={value}
        changeRequestView={changeRequestView}
      />
    );
  }
  return <>{formatValue(fieldKey, value)}</>;
}

const ENUM_VALUE_PATTERN = /^[A-Z][A-Z_]+$/;

const useFormatValue = (changeRequestView: ChangeRequestView) => {
  const { t } = useTranslation();
  return (key: string, value: unknown): ReactNode => {
    const formatted = formatChangeRequestValue(key, value, changeRequestView);
    if (typeof formatted !== "string" || !ENUM_VALUE_PATTERN.test(formatted)) {
      return formatted;
    }
    const labelKey = `storeChatSessions.changeRequest.values.${formatted}`;
    const label = t(labelKey);
    return label === labelKey ? formatted : label;
  };
};

const useFieldLabel = () => {
  const { t } = useTranslation();
  return (key: string) => {
    const labelKey = `storeChatSessions.changeRequest.fields.${key}`;
    const label = t(labelKey);
    return label === labelKey ? humanizeFieldKey(key) : label;
  };
};

const HIDDEN_CREATE_FIELDS = new Set(["name"]);

const isEmptyValue = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  value === false ||
  value === "" ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0);

function CreateFieldRows({
  changeRequestView,
  record,
}: {
  changeRequestView: ChangeRequestView;
  record: Record<string, unknown>;
}) {
  const fieldLabel = useFieldLabel();
  const formatValue = useFormatValue(changeRequestView);
  const keys = Object.keys(record).filter(
    (key) =>
      !HIDDEN_CREATE_FIELDS.has(key) &&
      !isEmptyValue(record[key]) &&
      !(key === "type" && record[key] === ProductType.PRODUCT),
  );
  const itemKeys = keys.filter((key) => LAYOUT_ITEM_FIELDS.has(key));
  const inlineKeys = keys.filter((key) => !LAYOUT_ITEM_FIELDS.has(key));
  return (
    <>
      {inlineKeys.length > 0 && (
        <InlineFields>
          {inlineKeys.map((key) => (
            <InlineField key={key}>
              <FieldLabel>{fieldLabel(key)}</FieldLabel>
              <FieldValue>{formatValue(key, record[key])}</FieldValue>
            </InlineField>
          ))}
        </InlineFields>
      )}
      {itemKeys.map((key) => (
        <ItemsBlock key={key}>
          <FieldLabel>{fieldLabel(key)}</FieldLabel>
          <FieldValueContent
            changeRequestView={changeRequestView}
            fieldKey={key}
            value={record[key]}
          />
        </ItemsBlock>
      ))}
    </>
  );
}

function ProductCreateTable({
  changeRequestView,
  operations,
}: {
  changeRequestView: ChangeRequestView;
  operations: ChangeRequestOperation[];
}) {
  const fieldLabel = useFieldLabel();
  const { t } = useTranslation();
  const rows = operations.flatMap((operation) => {
    const record = getOperationCreateRecord(operation);
    return record === null ? [] : [{ operation, record }];
  });
  return (
    <Table>
      <thead>
        <tr>
          <th>{fieldLabel("name")}</th>
          <th>{fieldLabel("unitPrice")}</th>
          <th>{fieldLabel("VAT")}</th>
          <th>{fieldLabel("groupId")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(({ operation, record }) => {
          const variants = getRecordFieldValue(record, "variants");
          const variantCount = Array.isArray(variants) ? variants.length : 0;
          return (
            <tr key={operation.operationId}>
              <td>
                {String(record.name ?? "")}
                {variantCount > 0 && (
                  <PreviewMeta>
                    {" "}
                    {t("storeChatSessions.changeRequest.variantCount", {
                      count: variantCount,
                    })}
                  </PreviewMeta>
                )}
              </td>
              <td>
                {formatChangeRequestValue(
                  "unitPrice",
                  record.unitPrice,
                  changeRequestView,
                )}
              </td>
              <td>
                {formatChangeRequestValue("VAT", record.VAT, changeRequestView)}
              </td>
              <td>
                {formatChangeRequestValue(
                  "groupId",
                  record.groupId,
                  changeRequestView,
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

function UpdateDiffRows({
  changeRequestView,
  beforeRecord,
  changes,
}: {
  changeRequestView: ChangeRequestView;
  beforeRecord: Record<string, unknown> | undefined;
  changes: Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const fieldLabel = useFieldLabel();
  const formatValue = useFormatValue(changeRequestView);
  const changeKeys = Object.keys(changes).filter(
    (key) => changes[key] !== undefined,
  );

  if (changeKeys.length === 0) {
    return null;
  }

  return (
    <DiffTable>
      <DiffHeader>
        <div />
        <div>{t("storeChatSessions.changeRequest.before")}</div>
        <div />
        <div>{t("storeChatSessions.changeRequest.after")}</div>
      </DiffHeader>
      {changeKeys.map((key) => (
        <DiffRow key={key}>
          <FieldLabel>{fieldLabel(key)}</FieldLabel>
          <BeforeValue>
            {formatValue(key, getRecordFieldValue(beforeRecord, key))}
          </BeforeValue>
          <Arrow>→</Arrow>
          <AfterValue>
            <FieldValueContent
              changeRequestView={changeRequestView}
              fieldKey={key}
              value={changes[key]}
            />
          </AfterValue>
        </DiffRow>
      ))}
    </DiffTable>
  );
}

function OperationDetailCard({
  changeRequestView,
  operation,
}: {
  changeRequestView: ChangeRequestView;
  operation: ChangeRequestOperation;
}) {
  const { t } = useTranslation();
  const previewName = getOperationPreviewName(changeRequestView, operation);
  const createRecord = getOperationCreateRecord(operation);
  const changes = getOperationChanges(operation);
  const beforeRecord = getOperationBeforeRecord(changeRequestView, operation);

  return (
    <Card>
      <CardTitle>
        {previewName.length > 0
          ? `${t(getOperationTypeLabelKey(operation.type))} · ${previewName}`
          : t(getOperationTypeLabelKey(operation.type))}
      </CardTitle>
      {createRecord !== null && (
        <CreateFieldRows
          changeRequestView={changeRequestView}
          record={createRecord}
        />
      )}
      {changes !== null && (
        <UpdateDiffRows
          changeRequestView={changeRequestView}
          beforeRecord={beforeRecord}
          changes={changes}
        />
      )}
    </Card>
  );
}

type Props = {
  changeRequest: ChangeRequestView;
  onApprove?: () => void;
  isApproving?: boolean;
  approveError?: Error | null;
};

export function ChangeRequestBubble({
  changeRequest,
  onApprove,
  isApproving,
  approveError,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();
  const fieldLabel = useFieldLabel();

  const operations = changeRequest.operations ?? [];
  const appliedOperationIds = new Set(
    (changeRequest.appliedOperations ?? []).map(
      (appliedOperation) => appliedOperation.operationId,
    ),
  );
  const isPartiallyApplied =
    changeRequest.status === ChangeRequestStatus.PARTIALLY_APPLIED;
  const groups = groupOperationsByDomain(operations);

  return (
    <Wrapper>
      <HeaderRow type="button" onClick={() => setExpanded((value) => !value)}>
        <Label>{t("storeChatSessions.changeRequest.title")}</Label>
        <StatusPill $status={changeRequest.status}>
          {t(
            `storeChatSessions.changeRequest.statuses.${changeRequest.status}`,
          )}
        </StatusPill>
        <Spacer />
        <Chevron>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </Chevron>
      </HeaderRow>

      {!expanded && (
        <PreviewList>
          {groups.map((group) => (
            <DomainSection key={group.domain}>
              <DomainHeading>
                {t(getDomainLabelKey(group.domain))} ({group.operations.length})
              </DomainHeading>
              {group.operations
                .slice(0, PREVIEW_LIMIT_PER_DOMAIN)
                .map((operation) => {
                  const kind = getChangeRequestOperationKind(operation.type);
                  const previewName = getOperationPreviewName(
                    changeRequest,
                    operation,
                  );
                  const changes = getOperationChanges(operation);
                  const changeSummary =
                    changes !== null
                      ? summarizeChanges(changes, fieldLabel)
                      : "";
                  return (
                    <PreviewLine key={operation.operationId} $kind={kind}>
                      <PreviewBadge $kind={kind}>
                        {kind === "create"
                          ? "+"
                          : kind === "delete"
                            ? "−"
                            : "~"}
                      </PreviewBadge>
                      <PreviewName>
                        {previewName.length > 0
                          ? previewName
                          : t(getOperationTypeLabelKey(operation.type))}
                      </PreviewName>
                      {changeSummary.length > 0 && (
                        <PreviewMeta>({changeSummary})</PreviewMeta>
                      )}
                      {appliedOperationIds.has(operation.operationId) && (
                        <AppliedMark
                          title={t("storeChatSessions.changeRequest.applied")}
                        >
                          ✓
                        </AppliedMark>
                      )}
                    </PreviewLine>
                  );
                })}
              {group.operations.length > PREVIEW_LIMIT_PER_DOMAIN && (
                <PreviewMeta>
                  {t("storeChatSessions.changeRequest.more", {
                    count: group.operations.length - PREVIEW_LIMIT_PER_DOMAIN,
                  })}
                </PreviewMeta>
              )}
            </DomainSection>
          ))}
        </PreviewList>
      )}

      {expanded && (
        <Details>
          {groups.map((group) => (
            <DomainSection key={group.domain}>
              <DomainHeading>
                {t(getDomainLabelKey(group.domain))}
              </DomainHeading>
              {group.operations.some(isProductCreate) && (
                <Card>
                  <ProductCreateTable
                    changeRequestView={changeRequest}
                    operations={group.operations.filter(isProductCreate)}
                  />
                </Card>
              )}
              {group.operations
                .filter((operation) => !isProductCreate(operation))
                .map((operation) => (
                  <OperationDetailCard
                    key={operation.operationId}
                    changeRequestView={changeRequest}
                    operation={operation}
                  />
                ))}
            </DomainSection>
          ))}
        </Details>
      )}

      <ActionsArea>
        {isPartiallyApplied && (
          <Hint>
            {t("storeChatSessions.changeRequest.partiallyAppliedHint")}
          </Hint>
        )}
        <ApproveRow>
          {isChangeRequestApprovable(changeRequest.status) && onApprove && (
            <ApproveButton
              type="button"
              onClick={onApprove}
              disabled={isApproving}
            >
              {isApproving
                ? t("storeChatSessions.changeRequest.approving")
                : isPartiallyApplied
                  ? t("storeChatSessions.changeRequest.retry")
                  : t("storeChatSessions.changeRequest.approve")}
            </ApproveButton>
          )}
          <ShowDetailsButton
            type="button"
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded
              ? t("storeChatSessions.changeRequest.hideDetails")
              : t("storeChatSessions.changeRequest.showDetails")}
          </ShowDetailsButton>
        </ApproveRow>
        {approveError && (
          <ApproveError>
            {approveError instanceof Error
              ? formatChangeRequestApproveError(approveError, t)
              : t("storeChatSessions.changeRequest.approveFailed")}
          </ApproveError>
        )}
      </ActionsArea>
    </Wrapper>
  );
}
